import React, { useState, useEffect } from 'react';
import { QueryClientProvider } from '@tanstack/react-query';
import { useConnectionQuality } from '@/hooks/useConnectionQuality';
import { useOnlineStatus } from '@/utils/connectionUtils';
import { createQueryClient, defaultQueryOptions } from '@/lib/queryConfig';

interface QueryProviderProps {
  children: React.ReactNode;
}

export const QueryProvider: React.FC<QueryProviderProps> = ({ children }) => {
  console.log("QueryProvider: render start");
  const { isSlowConnection } = useConnectionQuality();
  const isOnline = useOnlineStatus();
  
  // Create QueryClient with enhanced configuration
  const [queryClient] = useState(() => {
    const client = createQueryClient();
    
    // Set initial options based on connection quality
    client.setDefaultOptions({
      queries: {
        ...defaultQueryOptions,
        staleTime: isSlowConnection ? 1000 * 60 * 10 : 1000 * 60 * 5,
        gcTime: isSlowConnection ? 1000 * 60 * 20 : 1000 * 60 * 10,
        retry: isSlowConnection ? 1 : 2,
        refetchOnWindowFocus: import.meta.env.PROD && isOnline,
        refetchOnReconnect: isOnline,
        refetchOnMount: isOnline && !isSlowConnection,
      },
    });
    
    return client;
  });
  
  // Update query client settings when connection status changes
  useEffect(() => {
    queryClient.setDefaultOptions({
      queries: {
        ...defaultQueryOptions,
        staleTime: isSlowConnection ? 1000 * 60 * 10 : 1000 * 60 * 5,
        gcTime: isSlowConnection ? 1000 * 60 * 20 : 1000 * 60 * 10,
        retry: isSlowConnection ? 1 : 2,
        refetchOnWindowFocus: import.meta.env.PROD && isOnline,
        refetchOnReconnect: isOnline,
        refetchOnMount: isOnline && !isSlowConnection,
      },
    });
  }, [isSlowConnection, isOnline, queryClient]);

  return (
    <QueryClientProvider client={queryClient}>
      {children}
    </QueryClientProvider>
  );
};
